import React, { forwardRef, Ref, useMemo } from 'react';

import transparentize from 'polished/lib/color/transparentize';
import uniqid from 'uniqid';

import Flex, { FlexProps } from '@gojob/ui-bits/Flex';
import useLazyRef from '@gojob/ui-bits/hooks/useLazyRef';
import { Color } from '@gojob/ui-bits/styles/theme';
import useTheme from '@gojob/ui-bits/styles/useTheme';

export interface RoundedProps extends Omit<FlexProps<'div'>, 'ref' | 'color'> {
  color?: Color;
  zIndex?: number;
}

const useStyles = (color: Color, zIndex?: number) => {
  const { spacing, colors } = useTheme();
  return useMemo(() => {
    return {
      root: {
        zIndex,
        boxSizing: 'border-box',
        position: 'relative',
        ':before': {
          position: 'absolute',
          content: '""',
          top: 0,
          height: `calc(100% - ${spacing(14)})`,
          left: 0,
          width: '100%',
          backgroundColor: colors[color],
        },
      },
      bottom: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        overflow: 'visible',
        height: spacing(14),
        width: '100%',
        color: colors[color],
      },
    } as const;
  }, [spacing, colors, color, zIndex]);
};

const Rounded = (props: RoundedProps, ref: Ref<HTMLDivElement>) => {
  const { color = 'lightBackground', children, zIndex, padding = [0, 0, 2], ...other } = props;

  const classes = useStyles(color, zIndex);
  const id = useLazyRef(() => uniqid());

  const { shadowColors } = useTheme();
  const floodColor = useMemo(() => {
    return transparentize(0.8)(shadowColors[color]);
  }, [color, shadowColors]);

  return (
    <Flex data-testid="RoundedContent" ref={ref} css={classes.root} padding={padding} {...other}>
      {children}
      <svg data-testid="Chin" css={classes.bottom} viewBox="0 0 375 105" preserveAspectRatio="none">
        <defs>
          <filter height="180%" id={id.current}>
            <feDropShadow dy="20" dx="4" stdDeviation="10" floodColor={floodColor} />
          </filter>
        </defs>
        <path
          d="M0 0h375v85.43C312.84 98.477 251.007 105 189.5 105 127.993 105 64.826 98.477 0 85.43V0z"
          fill="currentColor"
          fillRule="evenodd"
          filter={`url(#${id.current})`}
        />
      </svg>
    </Flex>
  );
};

export default forwardRef(Rounded);
